import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TextLayoutLine } from 'react-native';
import { TouchableOpacity } from 'react-native';

import { Text, TextProps } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { AppColors } from '@/components/lib/theme';
import { isWeb } from '@/utils/constants-platform-specific';

// the toggle is semibold, so reserve a little more than the body-weight character width
const TOGGLE_WIDTH_FACTOR = 1.1;

type TextStyleProps = {
    color?: AppColors;
    toggleColor?: AppColors;
    letterSpacing?: TextProps['letterSpacing'];
};

type MoreLessComponentProps = TextStyleProps & {
    truncatedText: string;
    fullText: string;
};

const MoreLessComponent = ({ truncatedText, fullText, color, toggleColor, letterSpacing }: MoreLessComponentProps) => {
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation(['common']);

    return (
        <Box>
            <Text color={color} variant={'bodySmall'} letterSpacing={letterSpacing}>
                {!expanded ? `${truncatedText}... ` : fullText + ' '}
                <TouchableOpacity onPress={() => setExpanded(!expanded)} style={styles.expandButton}>
                    <Text variant={'titleSmall'} color={toggleColor} letterSpacing={letterSpacing}>
                        {expanded ? t('common:see_less') : t('common:see_more')}
                    </Text>
                </TouchableOpacity>
            </Text>
        </Box>
    );
};

// Trims the last visible line just enough for the "... See more" toggle to fit beside it.
const clipLines = (lines: TextLayoutLine[], linesToTruncate: number, toggleLength: number) => {
    // the widest measured line is as close to the container width as the layout gets
    const containerWidth = Math.max(...lines.map(line => line.width));
    const visibleLines = lines.slice(0, linesToTruncate);
    const lastLine = visibleLines[visibleLines.length - 1];

    // average character width on the last line, to convert between points and characters
    const charWidth = lastLine.width / Math.max(lastLine.text.length, 1);
    const toggleWidth = toggleLength * charWidth * TOGGLE_WIDTH_FACTOR;
    const spareWidth = containerWidth - lastLine.width;
    // nothing to trim when the toggle already fits in the space left on the last line
    const charsToTrim = Math.max(0, Math.ceil((toggleWidth - spareWidth) / charWidth));

    const visibleText = visibleLines.map(line => line.text).join('');
    return visibleText.slice(0, visibleText.length - charsToTrim).trimEnd();
};

type Props = TextStyleProps & {
    text: string;
    linesToTruncate: number;
};

export const ExpandableText = ({
    text,
    linesToTruncate,
    color = 'text.secondary',
    toggleColor,
    letterSpacing,
}: Props) => {
    const { t } = useTranslation(['common']);
    const [clippedText, setClippedText] = useState('');

    return clippedText ? (
        <MoreLessComponent
            truncatedText={clippedText}
            fullText={text}
            color={color}
            toggleColor={toggleColor}
            letterSpacing={letterSpacing}
        />
    ) : (
        <Text
            variant={'bodySmall'}
            color={color}
            letterSpacing={letterSpacing}
            // onTextLayout only reports rendered lines, so render one extra to detect overflow.
            // react-native-web never fires it, so web shows the full text instead of a clamp with no toggle.
            numberOfLines={isWeb ? undefined : linesToTruncate + 1}
            ellipsizeMode={'tail'}
            onTextLayout={event => {
                const { lines } = event.nativeEvent;
                if (lines.length > linesToTruncate) {
                    setClippedText(clipLines(lines, linesToTruncate, `... ${t('common:see_more')}`.length));
                }
            }}
        >
            {text}
        </Text>
    );
};

const styles = StyleSheet.create({
    expandButton: {
        transform: [{ translateY: 3 }],
    },
});
